import React, {ReactElement, useCallback} from 'react'
import {useState} from 'react'
import {Lang} from 'yougile-lang'
import {SystemStickerType, systemStickerTypes, Tag} from 'yougile-model'
import {UiSlot} from '@api/components'
import {ContextDropdownProvider} from '@api/shared/context/context-dropdown'
import {Icon} from '@components/common/base/icon'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {TablerIcons} from '@components/common/icon-tabler/icon-tabler'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {
  TwHeader,
  TwPinStickerMenu,
  TwStickersList,
  TwStickerTitle,
  TwStickerWrapper
} from '@components/sticker/pin-sticker-menu/containers'
import {Sticker as StickerExtension} from '@extensions/sticker/sticker'

interface PinStickerMenuProps {
  withHeader?: boolean
  stickers: Tag[]
  onClickOnSticker: (sticker: Tag) => void
  children: ReactElement
}

export const PinStickersDropdown = ({
  withHeader,
  stickers,
  onClickOnSticker,
  children
}: PinStickerMenuProps) => {
  const [isVisible, setIsVisible] = useState(false)

  const onClick = (sticker: Tag) => {
    if (onClickOnSticker) {
      onClickOnSticker(sticker)
    }
    setIsVisible(false)
  }

  const setVisibility = useCallback((visible: boolean) => {
    setIsVisible(visible)
  }, [])

  if (!stickers.length) {
    return null
  }

  return (
    <ContextDropdownProvider
      isVisible={isVisible}
      setVisibility={setVisibility}
    >
      <Dropdown
        visible={isVisible}
        animation={false}
        arrow={true}
        onVisibleChange={() => setIsVisible(false)}
        content={
          <PinStickersMenu
            withHeader={withHeader}
            stickers={stickers}
            onClick={onClick}
          />
        }
      >
        <div onClick={() => setIsVisible(true)}>
          {React.cloneElement(children, {
            $menuIsVisible: isVisible
          })}
        </div>
      </Dropdown>
    </ContextDropdownProvider>
  )
}

interface PinStickersMenuProps {
  withHeader?: boolean
  stickers: Tag[]
  onClick: (sticker: Tag) => void
}

const StickerMenuItem = ({
  icon,
  title,
  onClick
}: {
  icon?: TablerIcons | ReactElement
  title: ReactElement | string
  onClick: () => void
}) => (
  <TwStickerWrapper data-test="list-of-task-stickers" onClick={onClick}>
    {icon && (
      <Icon
        color={icon ? undefined : 'placeholder'}
        icon={icon || 'IconCustomBan'}
        className="group-hover:text-on-color"
      />
    )}
    <TwStickerTitle>{title}</TwStickerTitle>
  </TwStickerWrapper>
)

export const PinStickersMenu = ({
  withHeader = true,
  stickers,
  onClick
}: PinStickersMenuProps) => {
  return (
    <TwPinStickerMenu>
      {withHeader && <TwHeader>{Lang.msg.stickerPanel.menuTitle}</TwHeader>}
      <WithPerfectScrollbar scrollDirection="y">
        <TwStickersList>
          <UiSlot
            name="sticker-panel-menu-start"
            data={{
              StickerMenuItem
            }}
          />
          {stickers.map(sticker => {
            const icon = StickerExtension.getIcon(sticker)
            const stickerType = StickerExtension.getType(sticker)
            const isSystem = systemStickerTypes.includes(
              stickerType as SystemStickerType
            )
            const stickerTitle = isSystem
              ? Lang.msg.stickerNames[stickerType as SystemStickerType]
              : sticker.title

            return (
              <StickerMenuItem
                key={sticker.id}
                icon={icon}
                title={stickerTitle}
                onClick={() => onClick(sticker)}
              />
            )
          })}
          <UiSlot
            name="sticker-panel-menu-end"
            data={{
              StickerMenuItem
            }}
          />
        </TwStickersList>
      </WithPerfectScrollbar>
    </TwPinStickerMenu>
  )
}
